/**
 * Line-item amount helpers shared by the estimate / invoice / closed-order pages.
 *
 * Stored item fields (amount, gst_amount, total_amount, subtotal) are derived
 * values and can be stale or zero on older records (e.g. amount = 0 with
 * gst_amount = 3600). Whenever items are loaded into an editor or sent in a
 * save / conversion payload, recompute them from the source fields instead of
 * trusting what was stored:
 *
 *   base    = qty * unit_price
 *   taxable = base - base * discount / 100   -> amount
 *   gst     = taxable * gst_rate / 100        -> gst_amount
 *   total   = taxable + gst                   -> total_amount (= subtotal)
 *
 * Wood Work items derive qty from nos x length x breadth (nos alone when a
 * dimension is missing) only when no positive quantity is present.
 * The backend applies the same rules (backend/utils/itemAmounts.js).
 */

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export const computeWoodWorkQty = (nos, length, breadth) => {
  const n = toNumber(nos);
  const l = toNumber(length);
  const b = toNumber(breadth);
  if (l === 0 || b === 0) return n;
  return parseFloat((n * l * b).toFixed(4));
};

/**
 * Compute derived amounts for one item without mutating it.
 * @returns {{ quantity:number, amount:number, gst_amount:number, total_amount:number, subtotal:number }}
 */
export const computeItemAmounts = (item = {}) => {
  let quantity = toNumber(item.quantity);
  if ((item.isWoodWork || item.is_wood_work) && quantity <= 0 &&
      item.nos !== undefined && item.nos !== null && item.nos !== '') {
    quantity = computeWoodWorkQty(item.nos, item.length, item.breadth);
  }
  const unitPrice = toNumber(
    item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== ''
      ? item.unit_price
      : item.unitPrice
  );
  const discount = toNumber(item.discount);
  const gstRate = toNumber(item.gst_rate);

  const base = quantity * unitPrice;
  const taxable = base - (base * discount) / 100;
  const gst = (taxable * gstRate) / 100;
  const total = taxable + gst;

  return {
    quantity,
    amount: round2(taxable),
    gst_amount: round2(gst),
    total_amount: round2(total),
    subtotal: round2(total)
  };
};

/**
 * Return a copy of the item with amount / gst_amount / total_amount / subtotal
 * recomputed. Input fields (quantity, unit_price, discount, gst_rate) are left
 * as-is, so in-progress edits such as an empty string are preserved, except that
 * a missing Wood Work quantity is filled from its dimensions.
 */
export const recalcItemAmounts = (item) => {
  if (!item || typeof item !== 'object') return item;
  const computed = computeItemAmounts(item);
  const result = {
    ...item,
    amount: computed.amount,
    gst_amount: computed.gst_amount,
    total_amount: computed.total_amount,
    subtotal: computed.subtotal
  };
  if (toNumber(item.quantity) <= 0 && computed.quantity > 0) {
    result.quantity = computed.quantity;
  }
  return result;
};

/** Recompute amounts for every item in a list (non-arrays pass through as []). */
export const recalcItemsAmounts = (items) =>
  (Array.isArray(items) ? items : []).map(recalcItemAmounts);
